// utils/copyTable.ts
/**
 * A widget's figures onto the clipboard as a table: tab-separated text for anything, and an HTML
 * table, which Excel, Sheets, PowerPoint and Docs paste as real cells. Tables are read as shown
 * (the formatted figures the client sees); charts supply their own rows.
 */

export interface CopyTable {
    columns: string[];
    rows: Array<Array<string | number>>;
    /** Said in the copy's confirmation when the table was narrowed: "3 of 24 regions, searching “auck”" */
    note?: string;
}

/** Controls, help and dev notes inside a header or cell: never part of the figures */
const NOT_DATA = 'button, .export-exclude, [role="button"], svg';

const cellText = (cell: Element): string => {
    const clone = cell.cloneNode(true) as Element;
    clone.querySelectorAll(NOT_DATA).forEach((n) => n.remove());
    return (clone.textContent ?? '').replace(/\s+/g, ' ').trim();
};

/** The first table inside `root`, read as shown; null if there is none or it has no rows */
export const readTable = (root: HTMLElement | null): CopyTable | null => {
    const table = root?.querySelector('table');
    if (!table) return null;
    const headRow = table.querySelector('thead tr:last-child');
    const columns = headRow ? Array.from(headRow.children).map(cellText) : [];
    // A row marked export-exclude is a message (nothing matched), not figures
    const rows = Array.from(table.querySelectorAll('tbody tr:not(.export-exclude)'))
        .map((tr) => Array.from(tr.children).map(cellText))
        .filter((r) => r.some((c) => c !== ''));
    if (!rows.length) return null;
    // Drop columns that are empty all the way down (the actions column, a spacer)
    const keep = (columns.length ? columns : rows[0]).map(
        (_, i) => (columns[i] ?? '') !== '' || rows.some((r) => (r[i] ?? '') !== '')
    );
    return {
        columns: columns.filter((_, i) => keep[i]),
        rows: rows.map((r) => r.filter((_, i) => keep[i])),
        note: table.getAttribute('data-copy-note') ?? undefined,
    };
};

/** A sketch estimate to three significant figures, as the dashboard shows it, but as a number for a spreadsheet */
export const estimate = (n: number): number => (n ? Number(n.toPrecision(3)) : 0);

const escapeHtml = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const toTsv = ({ columns, rows }: CopyTable): string =>
    [columns, ...rows].map((r) => r.map((c) => String(c).replace(/[\t\n]/g, ' ')).join('\t')).join('\n');

export const toHtml = ({ columns, rows }: CopyTable): string =>
    `<table><thead><tr>${columns.map((c) => `<th>${escapeHtml(c)}</th>`).join('')}</tr></thead><tbody>${rows
        .map((r) => `<tr>${r.map((c) => `<td>${escapeHtml(String(c))}</td>`).join('')}</tr>`)
        .join('')}</tbody></table>`;

export async function copyTableToClipboard(table: CopyTable): Promise<void> {
    const tsv = toTsv(table);
    if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
            new ClipboardItem({
                'text/plain': new Blob([tsv], { type: 'text/plain' }),
                'text/html': new Blob([toHtml(table)], { type: 'text/html' }),
            }),
        ]);
    } else {
        await navigator.clipboard.writeText(tsv);
    }
}
